import { Alert } from 'src/design/atoms/Alert';
import { Box } from 'src/design/atoms/Box';
import { Container } from 'src/design/atoms/Container';
import { Group } from 'src/design/atoms/Group';
import { List } from 'src/design/atoms/List';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Input } from 'src/design/atoms/Input';
import { Radio } from 'src/design/atoms/Radio';
import { Textarea } from 'src/design/atoms/Textarea';
import { TextInput } from 'src/design/atoms/TextInput';
import { BaseModal } from 'src/design/molecules/BaseModal';
import { useFormik } from 'formik';
import React from 'react';
import { MilestonePhaseItemDto } from 'src/api/FiretrolApiClient';
import {
  useCreateMilestoneItem,
  useDeleteMilestoneItem,
  useUpdateMilestoneItem,
} from 'src/api/queries/milestones/useMilestoneItemMutations';
import { IMilestonePhaseItemFormValues } from 'src/api/models/Milestone';
import { parse422Errors } from 'src/api/utils/errorUtils';
import { RoleSelectorV2 } from 'src/app/selectors/RoleSelectorV2';
import { InlineConfirmButton } from 'src/design/molecules/InlineConfirmButton';
import { ModalButton } from 'src/design/molecules/ModalButton';
import * as Yup from 'yup';
import { JobUserSelect } from 'src/app/selectors/JobUserSelectSingle';
import { getErrorMessage } from 'src/utils';
import { Links } from './Links';
import { PhaseSelector } from './PhaseSelector';

const validationSchema = Yup.object().shape({
  title: Yup.string().required().min(1, 'Title Required').label('Title'),
});

const initialValues: IMilestonePhaseItemFormValues = {
  phaseId: null,
  title: '',
  description: '',
  complete: false,
  assigneeUserId: null,
  assignedTo: [],
  requireAssociation: 'true',
  critical: 'true',
  milestoneTemplateId: null,
};

export interface IMilestonePhaseItemDialogProps {
  opened: boolean;
  onClose: () => void;
  onCreateComplete?: () => void;
  onUpdateComplete?: () => void;
  data?: MilestonePhaseItemDto | null;
  copyFrom?: MilestonePhaseItemDto | null;
  selectedJobId?: number;
  templateId?: string;
}

const toFlag = (value: string | undefined): 'true' | 'false' =>
  value?.toLowerCase() === 'true' ? 'true' : 'false';

const copyInitialValues = (
  source: MilestonePhaseItemDto,
  templateId: string | undefined,
): IMilestonePhaseItemFormValues => ({
  phaseId: source.phaseId ?? null,
  title: source.title ?? '',
  description: source.description ?? '',
  assignedTo: [...(source.assignedTo ?? [])],
  requireAssociation: toFlag(source.requireAssociation),
  critical: toFlag(source.critical),
  complete: false,
  assigneeUserId: source.assigneeUserId || null,
  milestoneTemplateId: templateId || null,
});

/** @registry Modal for creating/editing/deleting a milestone phase item, on both jobs and templates; `copyFrom` opens the create form prefilled from an existing item. */
export const MilestonePhaseItemDialog: React.FC<
  IMilestonePhaseItemDialogProps
> = ({
  opened,
  selectedJobId,
  templateId,
  onUpdateComplete,
  data,
  copyFrom,
  onClose,
  onCreateComplete,
}) => {
  const [error, setError] = React.useState<string | null>(null);
  const [validationMessages, setValidationMessages] = React.useState<string[]>(
    [],
  );
  const scope = { jobId: selectedJobId, templateId };
  const createItem = useCreateMilestoneItem();
  const updateItem = useUpdateMilestoneItem();
  const deleteItem = useDeleteMilestoneItem();

  const getRidOfItem = async () => {
    if (!data) return;
    await deleteItem.mutateAsync({ scope, itemId: data.id });
    onClose();
    formik.resetForm();
    onUpdateComplete?.();
  };

  React.useEffect(() => {
    if (opened) {
      setError(null);
      setValidationMessages([]);
    }
    if (templateId) {
      formik.setFieldValue('milestoneTemplateId', templateId);
    }
  }, [templateId, opened]);

  const formik = useFormik({
    initialValues: data
      ? {
          id: data.id,
          phaseId: data.phaseId,
          title: data.title,
          description: data.description,
          assignedTo: data.assignedTo,
          requireAssociation: data.requireAssociation,
          critical: data.critical,
          complete: data.complete,
          assigneeUserId: data.assigneeUserId || null,
          milestoneTemplateId: templateId || data.milestoneTemplateId || null,
        }
      : copyFrom
      ? copyInitialValues(copyFrom, templateId)
      : initialValues,
    validationSchema,
    validateOnMount: false,
    onSubmit: async (values) => {
      setError(null);
      setValidationMessages([]);
      try {
        if (data?.id) {
          await updateItem.mutateAsync({
            scope,
            itemId: data.id,
            request: values,
          });
          onUpdateComplete?.();
        } else {
          await createItem.mutateAsync({ scope, request: values });
          onCreateComplete?.();
        }

        onClose();
        formik.resetForm();
      } catch (err) {
        const parsed = parse422Errors(err);
        if (parsed) {
          setValidationMessages(parsed.messages);
        } else {
          setError(getErrorMessage(err));
        }
      }
    },
  });

  const { values } = formik;

  return (
    <BaseModal
      opened={opened}
      onClose={onClose}
      title={
        data
          ? 'Edit Milestone Item'
          : copyFrom
          ? 'Copy Milestone Item'
          : 'New Milestone Item'
      }
      size="xl"
    >
      <Container>
        <Stack gap="md">
          <Input.Wrapper label="Phase">
            <PhaseSelector
              value={values.phaseId}
              onChange={(value) => formik.setFieldValue('phaseId', value)}
              jobId={selectedJobId}
              templateId={templateId}
            />
          </Input.Wrapper>

          <TextInput
            label="Title"
            placeholder="Enter title"
            value={values.title}
            onChange={(e) => formik.setFieldValue('title', e.target.value)}
            error={formik.touched.title && formik.errors.title}
            required
          />

          <Textarea
            label="Description"
            placeholder="Enter description"
            value={values.description}
            onChange={(e) =>
              formik.setFieldValue('description', e.target.value)
            }
            autosize
            minRows={3}
          />

          <Input.Wrapper label="Assign To Role">
            <RoleSelectorV2
              value={values.assignedTo}
              initialValue={values.assignedTo}
              onChange={(roles) => formik.setFieldValue('assignedTo', roles)}
            />
          </Input.Wrapper>

          {selectedJobId && (
            <Input.Wrapper label="Assign To User">
              <JobUserSelect
                hideLabel
                small
                hideRole
                selectedUserId={formik.values.assigneeUserId}
                onChange={(value) => {
                  if (value) {
                    formik.setFieldValue('assigneeUserId', value.userId);
                  } else {
                    formik.setFieldValue('assigneeUserId', null);
                  }
                }}
              />
            </Input.Wrapper>
          )}

          <Group justify="space-between">
            <Radio.Group
              label="Require Association to Complete"
              value={values.requireAssociation}
              onChange={(value) =>
                formik.setFieldValue('requireAssociation', value)
              }
            >
              <Group mt="xs">
                <Radio value="true" label="Yes" />
                <Radio value="false" label="No" />
              </Group>
            </Radio.Group>

            <Radio.Group
              label="Critical"
              value={values.critical}
              onChange={(value) => formik.setFieldValue('critical', value)}
            >
              <Group mt="xs">
                <Radio value="true" label="Yes" />
                <Radio value="false" label="No" />
              </Group>
            </Radio.Group>
          </Group>

          {selectedJobId && (
            <Radio.Group
              label="Status"
              value={values.complete ? 'true' : 'false'}
              onChange={(value) =>
                formik.setFieldValue('complete', value === 'true')
              }
            >
              <Group>
                <Radio value="true" label="Complete" />
                <Radio value="false" label="Incomplete" />
              </Group>
            </Radio.Group>
          )}

          {selectedJobId && data && (
            <Links jobId={selectedJobId} itemId={data.id} />
          )}

          {validationMessages.length > 0 && (
            <Alert color="yellow" title="Unable to Save Milestone Item">
              <List size="sm">
                {validationMessages.map((m, idx) => (
                  <List.Item key={`${m}-${idx}`}>{m}</List.Item>
                ))}
              </List>
              <Text size="sm" c="bright" mt="md">
                Please fix the above issues and try again.
              </Text>
            </Alert>
          )}

          {error && <Alert color="red">{error}</Alert>}

          <Group justify="space-between" mt="md">
            {data ? (
              <InlineConfirmButton onConfirmClick={getRidOfItem} color="red">
                Delete
              </InlineConfirmButton>
            ) : (
              <Box></Box>
            )}

            <Group>
              <ModalButton onClick={onClose} color="dark" variant="light">
                Cancel
              </ModalButton>
              <ModalButton
                onClick={() => formik.handleSubmit()}
                disabled={!formik.isValid}
                loading={formik.isSubmitting}
              >
                Save
              </ModalButton>
            </Group>
          </Group>
        </Stack>
      </Container>
    </BaseModal>
  );
};
